import React, { useEffect } from 'react';

import { IMAGE_FORMAT_OPTIONS, SENDING_TYPE_OPTIONS } from '@vlabs/api-bindings/src/luna-client/constants';
import { defaultValues } from '@vlabs/pages/queue/defaultFormValues';
import { DroiForm } from '@vlabs/pages/video-analytics/streams/components/forms/sub-forms/DroiForm';
import validate from '@vlabs/shared/validators';
import { ButtonSwitch, ButtonSwitchGroup, Control, SettingsItemWrapper } from '@vlabs/uikit';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import st from './SharedSubFormStyles.module.sass';

const GTE_LTE_COUNT_VALUES = {
  count_gte: 0,
  count_lte: 99,
};

export const QueueSubForm = ({ fieldName }) => {
  const { t } = useTranslation();

  // FIXME: для того чтобы поля с ошибками подсвечивались в нужной группе они должны иметь вначале fieldName
  // из за этого пришлось задублировать обьект description и положить его внутрь аналитики, необходим рефакторинг, чтобы мы не приводили аналитики к обьекту
  const createFullName = (name) => (fieldName ? `${fieldName}.${name}` : name);

  const { register, setValue, control, formState: { errors } } = useFormContext();

  const zoneCountType = useWatch({ name: createFullName('description.zone_count_type') });

  useEffect(() => {
    if (zoneCountType === 'count_gte') {
      setValue(createFullName('parameters.parameters.people_count__gte'), GTE_LTE_COUNT_VALUES.count_gte);
    } else if (zoneCountType === 'count_lte') {
      setValue(createFullName('parameters.parameters.people_count__lte'), GTE_LTE_COUNT_VALUES.count_lte);
    }
  }, [zoneCountType]);

  const commonStylesProps = {
    className: st.SettingItem,
    titleClassName: st.SettingItem__Title,
    contentClassName: st.SettingItem__Content,
  };

  return (
    <>
      <SettingsItemWrapper
        title={t('videoAnalytics:form.queue form.count type')}
        {...commonStylesProps}
      >
        <Controller
          control={control}
          name={createFullName('description.zone_count_type')}
          render={({ field: { value, onChange } }) => {
            return (
              <ButtonSwitchGroup
                onChange={onChange}
                size="medium"
                value={value}
                variant="square"
              >
                <ButtonSwitch value="count_gte">{t('videoAnalytics:form.queue form.greater than')}</ButtonSwitch>
                <ButtonSwitch value="count_lte">{t('videoAnalytics:form.queue form.less than')}</ButtonSwitch>
              </ButtonSwitchGroup>
            );
          }}
        />
      </SettingsItemWrapper>

      <SettingsItemWrapper
        title={t('videoAnalytics:form.queue form.people count')}
        {...commonStylesProps}
      >
        <Control.Input
          {...register(createFullName('description.zone_count_threshold'), {
            min: validate.gte(0),
            max: validate.lte(999),
            validate: validate.integer(),
          })}
          errors={errors}
        />
      </SettingsItemWrapper>

      <SettingsItemWrapper
        data-tooltip-content={t('videoAnalytics:form.queue form.event interval.tooltip')}
        title={t('videoAnalytics:form.queue form.event interval.label')}
        {...commonStylesProps}
      >
        <Control.Input
          {...register(createFullName('parameters.parameters.event_policy.interval'), {
            min: validate.gte(0),
            validate: {
              number: validate.number(),
              validateInterval: (value) => {
                if (!value) {
                  return validate.required();
                }
                return true;
              },
            },
          })}
          errors={errors}
        />
      </SettingsItemWrapper>

      <SettingsItemWrapper
        title={t('videoAnalytics:form.ROI.label')}
        {...commonStylesProps}
      >
        <Controller
          control={control}
          name={createFullName('parameters.parameters.roi')}
          render={({ field: { value, onChange } }) => {
            return (
              <Control.CoordinatesInput
                errors={errors}
                name={createFullName('parameters.parameters.roi')}
                onChange={onChange}
                value={value ?? defaultValues.queue.analytics[0].parameters.parameters.roi}
                variant='modern'
              />
            );
          }}
          rules={{ validate: validate.region }}
        />
      </SettingsItemWrapper>

      <DroiForm name={createFullName('parameters.parameters.droi')} />

      <SettingsItemWrapper
        title={t('videoAnalytics:form.image_retain_policy.mimetype.label')}
        {...commonStylesProps}
      >
        <Control.Select
          control={control}
          isClearable
          name={createFullName('parameters.parameters.image_retain_policy.mimetype')}
          options={IMAGE_FORMAT_OPTIONS.raw}
        />
      </SettingsItemWrapper>

      <SettingsItemWrapper
        title={t('videoAnalytics:form.image_retain_policy.quality.label')}
        {...commonStylesProps}
      >
        <Control.Input
          {...register(createFullName('parameters.parameters.image_retain_policy.quality'), {
            min: validate.gte(0),
            max: validate.lte(1),
            validate: validate.number(),
          })}
          errors={errors}
        />
      </SettingsItemWrapper>

      <SettingsItemWrapper
        title={t('videoAnalytics:form.image_retain_policy.max_size.label')}
        {...commonStylesProps}
      >
        <Control.Input
          {...register(createFullName('parameters.parameters.image_retain_policy.max_size'), {
            min: validate.gte(0),
            validate: validate.integer(),
          })}
          errors={errors}
        />
      </SettingsItemWrapper>

      <SettingsItemWrapper
        title={t('videoAnalytics:form.rate.unit.label')}
        {...commonStylesProps}
      >
        <Control.Select
          control={control}
          isClearable
          name={createFullName('parameters.parameters.rate.unit')}
          options={SENDING_TYPE_OPTIONS.raw}
        />
      </SettingsItemWrapper>

      <SettingsItemWrapper
        title={t('videoAnalytics:form.rate.period.label')}
        {...commonStylesProps}
      >
        <Control.Input
          {...register(createFullName('parameters.parameters.rate.period'), {
            min: validate.gte(1),
            validate: validate.integer(),
          })}
          errors={errors}
        />
      </SettingsItemWrapper>
    </>
  );
};
